import { useConst, Text } from '@chakra-ui/react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CategoryChoose } from '../../../category-tree/components/CategoryChoose/CategoryChoose';
import { CategoryTreeContextProvider } from '../../../category-tree/contexts/CategoryTreeContext';
import {
    type CategoryTreeItemType,
    type FeaturedSubcategoryType,
} from '../../../category-tree/hooks/useCategoryTreeStore';
import { type HtmlFormInput } from '../../hooks/useHtmlFormInputs';

export type FastCategoryTreeInitialData = {
    featured: FeaturedSubcategoryType[];
    categories: CategoryTreeItemType[];
};

export type FastCategoryTreeProps = {
    initial: string;
    input: HtmlFormInput<number | null>;
};

export function FastCategoryTree({ initial, input }: FastCategoryTreeProps) {
    const initialData = useConst<FastCategoryTreeInitialData>(JSON.parse(initial));
    const featured = useConst(Object.values(initialData?.featured ?? {}));
    const categories = useConst(Object.values(initialData?.categories ?? {}));
    const { t } = useTranslation();
    const {
        control,
        formState: { isSubmitted },
    } = useFormContext();

    const {
        field: { onChange: onChangeField },
        fieldState: { error },
    } = useController({
        name: 'subcategory',
        control,
        rules: {
            required: t('common.errors.required'),
        },
    });

    const onChange = (subcategory: number | null) => {
        input.onChange(subcategory);
        onChangeField(subcategory);
    };

    return (
        <CategoryTreeContextProvider
            featured={featured}
            categories={categories}
            initialSubcategory={input.defaultValue}
            onChange={onChange}
        >
            <CategoryChoose />
            {isSubmitted && error?.message !== undefined && (
                <Text color="red.500" fontSize="sm" mt={2}>
                    {error?.message}
                </Text>
            )}
        </CategoryTreeContextProvider>
    );
}
